/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import * as React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate } from 'react-router-dom';
import TranslateIcon from '@mui/icons-material/Translate';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import EditRoad from '@mui/icons-material/EditRoad';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import { Collapse, Divider, List } from '@mui/material';
import { ExpandLess, ExpandMore, Tune } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import RuleIcon from '@mui/icons-material/Rule';
import {
  ASSISTANT_TABS,
  aiAssistant,
} from '@ink-ai/portal/reducers/ai-assistant';
import { inspectActions } from '@ink-ai/portal/reducers/inspect';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useTranslation } from 'react-i18next';
import { Bolt, BookOpenText, BrainCircuit, Cable, House } from 'lucide-react';
import { styled } from '@mui/system';

const MnuItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

export const MainListItems = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [customOpen, setCustomOpen] = React.useState(true);
  const appState = useSelector((state: RootState) => state.app);
  const dispatch = useDispatch<any>();

  const handleClick = () => {
    setCustomOpen(!customOpen);
  };

  return (
    <React.Fragment>
      <ListItemButton onClick={() => navigate('/')}>
        <MnuItemIcon>
          <House />
        </MnuItemIcon>
        <ListItemText primary={t('common.home')} />
      </ListItemButton>
      <ListItemButton onClick={() => navigate('/articles')}>
        <MnuItemIcon>
          <HistoryEduIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.medicalWriter')} />
      </ListItemButton>
      <ListItemButton onClick={() => navigate('/translate-tasks')}>
        <MnuItemIcon>
          <TranslateIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.medicalTranslator')} />
      </ListItemButton>
      <ListItemButton
        onClick={() => {
          dispatch(aiAssistant.actions.setCurrentTab(ASSISTANT_TABS.INSPECT));
          dispatch(inspectActions.changeInspectType('rule'));
          navigate('/articles/new');
        }}
      >
        <MnuItemIcon>
          <RuleIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.ruleChecking')} />
      </ListItemButton>
      <ListItemButton
        onClick={() => {
          dispatch(aiAssistant.actions.setCurrentTab(ASSISTANT_TABS.INSPECT));
          dispatch(inspectActions.changeInspectType('fact'));
          navigate('/articles/new');
        }}
      >
        <MnuItemIcon>
          <FactCheckOutlinedIcon />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.factChecking')} />
      </ListItemButton>
      <Divider />
      <ListItemButton onClick={handleClick}>
        {appState.leftDrawerOpen ? (
          <ListItemText primary={t('navigation.customization')} />
        ) : (
          <MnuItemIcon>
            <Tune />
          </MnuItemIcon>
        )}
        {appState.leftDrawerOpen &&
          (customOpen ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
      <Collapse in={customOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItemButton onClick={() => navigate('/glossary')}>
            <MnuItemIcon>
              <BookOpenText />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.glossary')} />
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/reference-store')}>
            <MnuItemIcon>
              <LibraryBooksOutlinedIcon />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.referenceStore')} />
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/writing-instruction')}>
            <MnuItemIcon>
              <EditRoad />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.writingInstruction')} />
          </ListItemButton>
          <ListItemButton onClick={() => navigate('/writing-rule')}>
            <MnuItemIcon>
              <RuleFolderOutlinedIcon />
            </MnuItemIcon>
            <ListItemText primary={t('navigation.writingRule')} />
          </ListItemButton>
        </List>
      </Collapse>
      <Divider />
      <ListItemButton onClick={() => navigate('/workflows')}>
        <MnuItemIcon>
          <BrainCircuit />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.workflows')} />
      </ListItemButton>
      <ListItemButton onClick={() => navigate('/settings/connectors')}>
        <MnuItemIcon>
          <Cable />
        </MnuItemIcon>
        <ListItemText primary={t('navigation.connectors')} />
      </ListItemButton>
      <ListItemButton onClick={() => navigate('/settings')}>
        <MnuItemIcon>
          <Bolt />
        </MnuItemIcon>
        <ListItemText primary={t('common.settings')} />
      </ListItemButton>
    </React.Fragment>
  );
};
